import React, { ReactElement, useState, useEffect } from "react";
import { Redirect } from "react-router-dom";
import { DASHBOARD } from "constants/route/route-constants";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import { useAppSelector } from "redux-store/hooks";
import { RootState } from "redux-store/store";
import { brandEnum } from "slices/brand/brand-slice";

import LoaderButton from "shared-components/loader-button";
import { BTN_DISABLED, loaderBtnStyleFunc } from "constants/style/props";
import { CancelButton } from "pages/management/privacy-policy/components/details/styles";
import { InputLabelText } from "pages/user-profile/components/basic-info/styles";
import { LoadingComp } from "pages/news/components/news-details/styles";

import {
  BtnFieldContainer,
  CallCentreContainer,
  CardDiv,
  FieldContainer,
  HeaderDiv,
  HeadingDiv,
  InputTextField,
  LeftDiv,
  Required,
} from "./styles";

import {
  getCallCentreNumber,
  updateCallCentreNumber,
} from "services/call-centre-services";

const useStyles = makeStyles({
  disabled: BTN_DISABLED,
});

function CallCentre(): ReactElement {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const brand = useAppSelector((state: RootState) => state.brand.brandType);

  const [loading, setLoading] = useState<boolean>(false);
  const [btnLoading, setBtnLoading] = useState<boolean>(false);
  const [isUpdated, setIsUpdated] = useState<boolean>(false);

  const [number, setNumber] = useState<string>("");
  const [initialNumber, setInitialNumber] = useState<string>("");
  const [error, setError] = useState<string>("");

  const isToyota = brand === brandEnum.T;

  const fetchCallCentreNumber = async () => {
    setLoading(true);
    setError("");
    try {
      // Pass brand dynamically to support future multi-brand expansion
      const brandParam = brand ? brand.toLowerCase() : "toyota";
      const result = await getCallCentreNumber(brandParam);
      const fetchedNumber =
        result?.data?.data?.tmcConnectNumber ??
        result?.data?.tmcConnectNumber ??
        "";
      setNumber(fetchedNumber);
      setInitialNumber(fetchedNumber);
      setIsUpdated(false);
      setLoading(false);
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.data ||
        "Failed to fetch TMC connect contact number";
      enqueueSnackbar(errorMsg, { variant: "error" });
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isToyota) {
      fetchCallCentreNumber();
    }
    // eslint-disable-next-line
  }, [brand]);

  const validate = (val: string): boolean => {
    const trimmed = val.trim();
    if (!trimmed) {
      setError("TMC connect contact number is required");
      return false;
    }
    // Must start with '+' (optional) immediately followed by a digit, allowing 6-20 phone characters
    const phoneRegex = /^(\+?[0-9][0-9\s-()]{5,19})$/;
    if (!phoneRegex.test(trimmed)) {
      setError("Please enter a valid phone number (e.g. +973 17787878)");
      return false;
    }
    setError("");
    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;

    // Disallow leading spaces
    val = val.trimStart();

    // Restrict input to digits, spaces, hyphens, parentheses, and optional leading '+'
    val = val.replace(/[^0-9+\s\-()]/g, "");

    // Allow '+' only as the leading character
    if (val.includes("+")) {
      const startsWithPlus = val.startsWith("+");
      val = (startsWithPlus ? "+" : "") + val.replace(/\+/g, "");
    }

    // Disallow space immediately after '+' (e.g. '+ 973' -> '+973')
    val = val.replace(/^\+\s+/, "+");

    // Prevent consecutive spaces (collapse multiple spaces into one)
    val = val.replace(/\s{2,}/g, " ");

    setNumber(val);
    validate(val);
    setIsUpdated(val.trim() !== initialNumber.trim());
  };

  const handleSave = async () => {
    if (!validate(number)) {
      return;
    }
    if (!isUpdated) {
      return;
    }

    setBtnLoading(true);
    try {
      const payload = {
        tmcConnectNumber: number.trim(),
      };
      const result = await updateCallCentreNumber(payload);
      const successMsg =
        result?.data?.response ||
        result?.data?.message ||
        "TMC connect contact number updated successfully";

      enqueueSnackbar(successMsg, {
        variant: "success",
      });
      setInitialNumber(number.trim());
      setIsUpdated(false);
      setBtnLoading(false);
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.data ||
        err?.response?.data?.message ||
        "Failed to update TMC connect contact number";
      enqueueSnackbar(errorMsg, { variant: "error" });
      setBtnLoading(false);
    }
  };

  const handleCancel = () => {
    setNumber(initialNumber);
    setError("");
    setIsUpdated(false);
  };

  if (!isToyota) {
    return <Redirect to={DASHBOARD} />;
  }

  return (
    <CallCentreContainer>
      <HeaderDiv>
        <LeftDiv>
          <HeadingDiv>Call Centre Management</HeadingDiv>
        </LeftDiv>
      </HeaderDiv>
      <CardDiv>
        {loading ? (
          <LoadingComp color="primary" />
        ) : (
          <>
            <FieldContainer>
              <InputLabelText>
                TMC connect contact number<Required>*</Required>
              </InputLabelText>
              <InputTextField
                type="tel"
                inputProps={{
                  inputMode: "tel",
                  maxLength: 20,
                }}
                variant="outlined"
                size="small"
                placeholder="+973 17787878"
                value={number}
                onChange={handleChange}
                error={Boolean(error)}
                helperText={error}
              />
            </FieldContainer>

            <BtnFieldContainer>
              <LoaderButton
                isLoading={btnLoading}
                buttonText="Update"
                styles={loaderBtnStyleFunc("7rem", "2.5rem")}
                submitHandler={handleSave}
                className={`${!isUpdated || Boolean(error) ? classes.disabled : ""}`}
              />
              <CancelButton
                variant="outlined"
                color="primary"
                onClick={handleCancel}
                disabled={!isUpdated || btnLoading}
              >
                Cancel
              </CancelButton>
            </BtnFieldContainer>
          </>
        )}
      </CardDiv>
    </CallCentreContainer>
  );
}

export default CallCentre;
